import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useAppDispatch, RootState } from '../../store';
import {
  selectColorError,
  selectColorLoading,
  selectedColors,
  fetchColors,
  createColor,
  editColor,
  resetForm,
  selectColorPagination,
  toggleColorStatus,
} from './colorSlice';
import { Color } from './color';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usePermission } from '../../components/common/PermissionGuard';
import PageWrapper from '../../components/common/PageWrapper';
import SearchBar from '../../components/searchbar';
import Pagination from '../../components/common/Pagination';
import {formatStatusText, capitalizeWords} from '../../utils/formatters'
import ChangeStatusConfirmation from "../../components/common/__tests__/ChangeStatusConfirmation";

const Colors: React.FC = () => {
  const dispatch = useAppDispatch();
  const colors = useSelector(selectedColors);
  const loading = useSelector(selectColorLoading);
  const error = useSelector(selectColorError);
  const pagination = useSelector(selectColorPagination)

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<Color | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [colorName, setColorName] = useState('');
  const [colorStatus, setColorStatus] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedColorForStatus, setSelectedColorForStatus] = useState<Color | null>(null);

  useEffect(() => {
    dispatch(fetchColors({ page: currentPage, limit: itemsPerPage, search: searchTerm }));
  }, [dispatch, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    console.log('Colors state updated:', colors);
  },[colors]);

  // Permissions
  const canCreate = usePermission('colors', 'create');
  const canUpdate = usePermission('colors', 'update');
  const canDelete = usePermission('colors', 'delete');
  const canRead = usePermission('colors', 'read');

  const handleCreateColor = () => {
    dispatch(resetForm());
    setSelectedColor(null);
    setFormMode('create');
    setColorName('');
    setColorStatus(1);
    setIsFormOpen(true);
  };

  const handleEditColor = (color: Color) => {
    setColorName(color.color_name);
    setColorStatus(color.status);
    setSelectedColor(color);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleFormSubmit = () => {
    const formData = { color_name: colorName, status: colorStatus };
    console.log('Submitting form with data:', formData);
    console.log('selectedColor:', selectedColor);

    if (formMode === 'create') {
      console.log('Creating new color');
      dispatch(createColor(formData));
    } else if (selectedColor?.id) {
      console.log('editing new color');
      dispatch(editColor({ id: selectedColor.id, data: formData }));
    }

    setIsFormOpen(false);
    setColorName('');
    setColorStatus(1);
    setSelectedColor(null);
  };

  const toggleStatus = async (color: Color) => {
    const newStatus = color.status === 1 ? 0 : 1;
    try {
      await dispatch(toggleColorStatus({ 
        id: color.colorId, 
        status: newStatus 
      })).unwrap();
      // Refresh the locations list after successful status update
      dispatch(fetchColors({ 
        page: currentPage, 
        limit: itemsPerPage, 
        search: searchTerm 
      }));
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  return (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Colors Management</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage colors for your application
            </p>
          </div>
          {canCreate && (
            <button
              onClick={handleCreateColor}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Color
            </button>
          )}
        </div>

        {/* Search */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <SearchBar
            placeholder="Search colors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-600">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated On</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {colors?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        No colors found. {canCreate && 'Create your first color to get started.'}
                      </td>
                    </tr>
                  ) : (
                    colors?.map((color) => (
                        <tr key={color.id}>
                          <td className="px-6 py-4 text-sm font-medium">{color.color_name}</td>
                          <td className="px-6 py-4 text-sm">
                            {(() => {
                              // Show createdOn if record was never updated (bulk uploaded)
                              // Otherwise show updatedOn
                              const displayDate = color?.updatedOn && 
                                color?.createdOn && 
                                dayjs(color.updatedOn).isAfter(dayjs(color.createdOn))
                                ? color.updatedOn 
                                : color?.createdOn || color?.updatedOn;
                              return displayDate ? dayjs(displayDate).format('DD/MM/YYYY HH:mm') : 'N/A';
                            })()}
                          </td>
                          <td className="px-6 py-4 text-sm">{color?.user_name || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm cursor-pointer" onClick={() => {setSelectedColorForStatus(color)
                          setIsStatusModalOpen(true)
                          }}>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                color.status === 1
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {color.status === 1 ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-sm">
                            {canUpdate && (
                              <button
                                onClick={() => handleEditColor(color)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative w-96 p-5 border shadow-lg rounded-md bg-white dark:bg-gray-800">
              <button
                onClick={() => setIsFormOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              <h3 className="text-lg font-medium mb-4">
                {formMode === 'create' ? 'Create Color' : 'Edit Color'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Color Name</label>
                  <input
                    type="text"
                    value={colorName}
                    onChange={(e) => setColorName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter color name"
                  />
                </div>

                {/* <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    value={colorStatus}
                    onChange={(e) => setColorStatus(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div> */}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 text-sm bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFormSubmit}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md"
                >
                  {formMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isStatusModalOpen && selectedColorForStatus && (
        <ChangeStatusConfirmation
          open={isStatusModalOpen}
          onClose={() => {
            setIsStatusModalOpen(false);
            setSelectedColorForStatus(null);
          }}
          onConfirm={() => {
            toggleStatus(selectedColorForStatus);
            setIsStatusModalOpen(false);
            setSelectedColorForStatus(null);
          }}
          title="Change Status"
          message={`Are you sure you want to change status to ${selectedColorForStatus.status === 1 ? 'Inactive' : 'Active'}?`}
        />
      )}

      {pagination && pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => {
            setCurrentPage(page);
            dispatch(fetchColors({ page, limit: itemsPerPage }));
          }}
          onItemsPerPageChange={(limit) => {
            setItemsPerPage(limit);
            setCurrentPage(1);
            dispatch(fetchColors({ page: 1, limit }));
          }}
          showItemsPerPage={true}
        />
      )}

    </PageWrapper>
  );
};

export default Colors;
